/**
 * 用户认证工具函数
 * 包含 token / 用户信息管理、收藏管理、试用申请等功能
 * 登录、注册、改密、用户信息等接口调用在对应页面组件中通过 this.$http 完成
 */

const TOKEN_KEY = 'token'
const USER_KEY = 'wb_user_info_v1'
const FAVS_KEY = 'wb_favs_v1'

/**
 * 获取登录 token
 */
export function getToken () {
  return localStorage.getItem(TOKEN_KEY) || ''
}

/**
 * 保存登录 token
 */
export function setToken (token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

/**
 * 获取登录用户信息（原始存储对象）
 */
export function getUserInfo () {
  try {
    const str = localStorage.getItem(USER_KEY)
    return str ? JSON.parse(str) : null
  } catch (e) {
    return null
  }
}

/**
 * 保存登录用户信息
 */
export function setUserInfo (user) {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(USER_KEY)
  }
}

/**
 * 获取当前登录用户（返回归一化后的对象，兼容昵称/用户名等旧字段）
 */
export function getCurrentUser () {
  const user = getUserInfo()
  if (!user) return null
  const name = user.realName || user.mobile || ''
  return {
    ...user,
    nickname: user.nickname || name,
    username: user.username || name,
    company: user.company || user.companyName || ''
  }
}

/**
 * 用户登出：清除 token 与用户信息并返回首页
 */
export function logout () {
  setToken(null)
  setUserInfo(null)
  window.location.href = '/'
}

/**
 * 获取收藏列表
 */
export function getFavorites () {
  const user = getCurrentUser()
  if (!user) return []

  try {
    const favs = JSON.parse(localStorage.getItem(FAVS_KEY) || '{}')
    return favs[user.id] || []
  } catch (e) {
    return []
  }
}

/**
 * 添加收藏
 * @param {Object} fav 收藏信息
 */
export function addFavorite (fav) {
  const user = getCurrentUser()
  if (!user) return { ok: false, msg: '请先登录' }

  const favs = JSON.parse(localStorage.getItem(FAVS_KEY) || '{}')
  if (!favs[user.id]) favs[user.id] = []

  // 检查是否已收藏
  const exists = favs[user.id].find(f => f.type === fav.type && f.id === fav.id)
  if (exists) {
    return { ok: false, msg: '已收藏' }
  }

  fav.savedAt = new Date().toISOString()
  favs[user.id].push(fav)
  localStorage.setItem(FAVS_KEY, JSON.stringify(favs))

  return { ok: true, msg: '收藏成功' }
}

/**
 * 移除收藏
 * @param {string} type 类型
 * @param {string} id ID
 */
export function removeFavorite (type, id) {
  const user = getCurrentUser()
  if (!user) return

  const favs = JSON.parse(localStorage.getItem(FAVS_KEY) || '{}')
  if (!favs[user.id]) return

  favs[user.id] = favs[user.id].filter(f => !(f.type === type && f.id === id))
  localStorage.setItem(FAVS_KEY, JSON.stringify(favs))
}

/**
 * 验证手机号
 * @param {string} mobile 手机号
 * @returns {boolean}
 */
export function validMobile (mobile) {
  return /^1[3-9]\d{9}$/.test(mobile)
}

/**
 * 格式化时间
 * @param {string} timeStr 时间字符串
 * @returns {string}
 */
export function formatTime (timeStr) {
  if (!timeStr) return '--'
  const date = new Date(timeStr)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d} ${h}:${min}`
}

/**
 * 计算天数差
 * @param {Date} start 开始日期
 * @param {Date} end 结束日期
 * @returns {number}
 */
export function daysBetween (start, end) {
  const diff = end.getTime() - start.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

/**
 * 转义HTML特殊字符
 * @param {string} str 字符串
 * @returns {string}
 */
export function escapeHtml (str) {
  if (!str) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * 获取问候语
 * @returns {string}
 */
export function getGreeting () {
  const h = new Date().getHours()
  if (h < 6) return '凌晨好，'
  if (h < 11) return '早上好，'
  if (h < 14) return '中午好，'
  if (h < 18) return '下午好，'
  return '晚上好，'
}

/**
 * 获取手机号后4位
 * @param {string} mobile 手机号
 * @returns {string}
 */
export function getLast4Mobile (mobile) {
  if (!mobile || mobile.length < 4) return '****'
  return '****' + mobile.slice(-4)
}

export default {
  getToken,
  setToken,
  getUserInfo,
  setUserInfo,
  getCurrentUser,
  logout,
  getFavorites,
  addFavorite,
  removeFavorite,
  validMobile,
  formatTime,
  daysBetween,
  escapeHtml,
  getGreeting,
  getLast4Mobile
}



// WEBPACK FOOTER //
// ./src/utils/auth.js